import {
  DamageTypeEnum,
  InjuredPersonTypeEnum,
  type NamedPerson,
  type ResidentInjuryDetailsRequest
} from '@lmc/validations'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IncidentCaseV2StepForm, { StepListItem } from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepField from '../StepField'
import { StepYesNoField } from '../StepYesNo'
import { StepSection, StepSeparator } from '../stepLayout'
import {
  type SavedPerson,
  useServiceUserOptions,
  useTeamMemberOptions,
  withSavedPeople
} from '../stepOptions'
import { toAnswered, trimmed } from '../stepValues'
import { useServiceUserWords } from '../useServiceUserWords'
import { type Step5Case, step5Answers } from './injuriesAndDamages/appInjuries'
import DamageEntries from './injuriesAndDamages/DamageEntries'
import InjuryEntries from './injuriesAndDamages/InjuryEntries'
import {
  type DamageEntryValue,
  hasPerson,
  type InjuryEntryValue,
  toDamageEntry,
  toInjury
} from './injuriesAndDamages/injuryEntryValues'

const MULTIPLE_BODY_MAPS_FLAG = 'incident_multiple_body_maps'
const testId = 'incident-case-v2-injuries-and-damages'

type InjuriesAndDamagesValues = {
  damageDetails?: string
  damageEntries: Array<DamageEntryValue>
  injuryDetails?: string
  injuryEntries: Array<InjuryEntryValue>
  isDamaged?: boolean
  isInjured?: boolean
}

/** The case fields this step reads; ts-common's case type lacks most of them. */
type SavedCase = Step5Case & {
  carersInvolved?: Array<SavedPerson>
  otherPartiesInvolved?: Array<NamedPerson>
  residentInjuries?: ResidentInjuryDetailsRequest | null
  serviceUsersInvolved?: Array<SavedPerson>
}

/**
 * Step 5: who was injured and how, and what was damaged. Injuries offer the
 * people from People involved, or the whole home when none were recorded. The
 * service user's body maps are saved in `residentInjuries`, where the case has
 * always kept them.
 */
const InjuriesAndDamagesStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const serviceUserWords = useServiceUserWords()
  const flags = useFeatureFlags({ flagNames: [MULTIPLE_BODY_MAPS_FLAG] })
  const showBodyMaps =
    !flags.isLoading && Boolean(flags.data[MULTIPLE_BODY_MAPS_FLAG]?.enabled)

  const { serviceUser } = incidentReport
  const savedCase = useMemo(
    () => (incidentReport.case ?? {}) as SavedCase,
    [incidentReport]
  )
  const homeServiceUsers = useServiceUserOptions({ excludeId: serviceUser.id })
  const knownServiceUsers = useServiceUserOptions({
    excludeId: serviceUser.id,
    includeInactive: true
  })
  const homeTeamMembers = useTeamMemberOptions({
    saved: (savedCase.injuries?.entries ?? []).flatMap((entry) => [
      entry.userId,
      entry.treatmentGivenBy
    ])
  })

  const involvedServiceUsers = withSavedPeople(
    [],
    savedCase.serviceUsersInvolved
  ).filter((option) => option.value !== serviceUser.id)
  const involvedTeamMembers = withSavedPeople([], savedCase.carersInvolved)

  const people = {
    otherParties: (savedCase.otherPartiesInvolved ?? []).map(
      (party) => party.name
    ),
    otherServiceUsers: involvedServiceUsers.length
      ? involvedServiceUsers
      : homeServiceUsers,
    teamMembers: involvedTeamMembers.length
      ? involvedTeamMembers
      : homeTeamMembers
  }
  const labels = new Map(
    [
      ...knownServiceUsers,
      ...homeTeamMembers,
      ...involvedServiceUsers,
      ...involvedTeamMembers
    ].map((option) => [option.value, option.label])
  )

  const savedBodyMaps = useMemo(
    () => savedCase.residentInjuries?.residentInjuriesBodyMaps ?? [],
    [savedCase]
  )

  const initialValues = useMemo<InjuriesAndDamagesValues>(() => {
    const { damages, injuries } = step5Answers(savedCase)

    return {
      damageDetails: damages?.hasBeenApplied ? undefined : damages?.details,
      damageEntries: damages?.entries ?? [],
      injuryDetails: injuries?.hasBeenApplied ? undefined : injuries?.details,
      injuryEntries: (injuries?.entries ?? []).map((entry) =>
        entry.personType === InjuredPersonTypeEnum.serviceUser
          ? { ...entry, bodyMaps: savedBodyMaps }
          : entry
      ),
      isDamaged: damages?.hasBeenApplied,
      isInjured: injuries?.hasBeenApplied
    }
  }, [savedCase, savedBodyMaps])

  const toStepValues = ({
    damageDetails,
    damageEntries,
    injuryDetails,
    injuryEntries,
    isDamaged,
    isInjured
  }: InjuriesAndDamagesValues): IncidentCaseV2StepValues => {
    const injuries = toAnswered(isInjured, injuryDetails, {
      entries: injuryEntries.map(toInjury)
    })
    // The v1 `damages.details` the app wrote under Yes, which v1 screens and
    // exports still read. Kept only while its damage-only entry is unchanged,
    // so an edited or deleted damage can't bring the old text back.
    const savedDamageDetails = savedCase.damages?.hasBeenApplied
      ? trimmed(savedCase.damages.details)
      : undefined
    const appDamageDetails = damageEntries.some(
      (entry) =>
        entry.type === DamageTypeEnum.damageOnly &&
        trimmed(entry.details) === savedDamageDetails
    )
      ? savedDamageDetails
      : undefined
    const damages = toAnswered(isDamaged, damageDetails, {
      ...(appDamageDetails ? { details: appDamageDetails } : {})
    })
    const serviceUserEntry = isInjured
      ? injuryEntries.find(
          (entry) => entry.personType === InjuredPersonTypeEnum.serviceUser
        )
      : undefined
    const bodyMaps = serviceUserEntry?.bodyMaps ?? []
    const { details: residentInjuryDetails } = savedCase.residentInjuries ?? {}

    return {
      ...(injuries && { injuries }),
      // Sent only when there are body maps to link or to unlink, so a case
      // without them keeps its `residentInjuries` as it was. Unlinking the
      // last map removes it, unless it still holds saved details.
      ...(bodyMaps.length || savedBodyMaps.length
        ? {
            residentInjuries:
              bodyMaps.length || residentInjuryDetails
                ? {
                    hasBeenApplied: Boolean(serviceUserEntry),
                    residentInjuriesBodyMaps: bodyMaps,
                    ...(residentInjuryDetails
                      ? { details: residentInjuryDetails }
                      : {})
                  }
                : null
          }
        : {}),
      ...(damages && {
        damageEntries: isDamaged ? damageEntries.map(toDamageEntry) : null,
        damages
      })
    }
  }

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection>
        <StepYesNoField
          confirmNo={{
            body: t(incidentReportsConstants.CASE_V2_INJURIES_NO_CONFIRM_BODY),
            isNeeded: (form) => form.getFieldValue('injuryEntries')?.length > 0,
            onConfirm: (form) => form.setFieldValue('injuryEntries', []),
            title: t(incidentReportsConstants.CASE_V2_INJURIES_NO_CONFIRM_TITLE)
          }}
          detailsLabel={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
          detailsName="injuryDetails"
          detailsTestId={`${testId}-injury-details`}
          label={t(
            incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYONE_INJURED
          )}
          name="isInjured"
          testId={`${testId}-is-injured`}
        >
          <StepListItem
            name="injuryEntries"
            rules={[
              {
                message: t(incidentReportsConstants.CASE_V2_FIELD_REQUIRED),
                required: true,
                type: 'array'
              },
              {
                // An injury from the app can arrive without its person.
                validator: (_, entries?: Array<InjuryEntryValue>) =>
                  (entries ?? []).every(hasPerson)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t(
                            incidentReportsConstants.CASE_V2_INJURIES_CHOOSE_PERSON
                          )
                        )
                      )
              }
            ]}
          >
            <InjuryEntries
              homePeople={{
                otherServiceUsers: homeServiceUsers,
                teamMembers: homeTeamMembers
              }}
              labels={labels}
              people={people}
              serviceUser={serviceUser}
              serviceUserWords={serviceUserWords}
              showBodyMaps={showBodyMaps}
              teamMemberOptions={homeTeamMembers}
              testId={`${testId}-injuries`}
            />
          </StepListItem>
        </StepYesNoField>
      </StepSection>

      <StepSeparator />

      <StepSection>
        <StepYesNoField
          confirmNo={{
            body: t(
              incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_NO_CONFIRM_BODY
            ),
            isNeeded: (form) => form.getFieldValue('damageEntries')?.length > 0,
            onConfirm: (form) => form.setFieldValue('damageEntries', []),
            title: t(
              incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_NO_CONFIRM_TITLE
            )
          }}
          detailsLabel={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
          detailsName="damageDetails"
          detailsTestId={`${testId}-damage-details`}
          label={t(
            incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYTHING_DAMAGED
          )}
          name="isDamaged"
          testId={`${testId}-is-damaged`}
        >
          <StepField
            name="damageEntries"
            isList
            isRequired
            label={t(
              incidentReportsConstants.CASE_V2_INJURIES_WHAT_WAS_DAMAGED
            )}
          >
            <DamageEntries testId={`${testId}-damages`} />
          </StepField>
        </StepYesNoField>
      </StepSection>
    </IncidentCaseV2StepForm>
  )
}

export default InjuriesAndDamagesStep
